import { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import type { SearchInterpretation } from "@clustec/common";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";
import { useNetworkStore } from "../stores/network";
import { useSearch } from "../api/hooks";
import { abbreviateHex } from "./TxTable";
import { Spinner } from "./ui";

/**
 * Global search.
 *
 * Offers interpretations rather than guessing, because one string genuinely means several things:
 * the same 64-hex field can be a tx hash, a nullifier, a note hash and a calldata constant all at
 * once, and a bare number is a plausible block, slot, epoch and checkpoint. The box used to guess
 * client-side from the shape of the input alone, which meant it offered readings that did not
 * exist (a slot nobody ever proposed) and missed the ones that did (a nullifier is invisible to a
 * regex). Every reading now comes from the server, which has actually looked, so a row in the
 * dropdown is a promise that the target page has something on it.
 *
 * The box stays a quick-jump: each row goes to one match, and the row's evidence names which one.
 * When a reading has more matches than a dropdown can hold, the final "See all" row hands the
 * query to the results page, which lists every one of them.
 *
 * The prefix hints (`tx:`, `address:`, `block:`, `slot:`, `epoch:`, `checkpoint:`) are passed
 * through verbatim; the server strips them and uses them to narrow which probes run.
 */

/** How long typing has to settle before we ask the server. */
const DEBOUNCE_MS = 300;

/**
 * One dropdown row: a kind badge, the thing that matched, and the evidence for why it matched.
 * `to` is null for rows that are information rather than navigation (the rollup contract), which
 * keeps them visible without pretending they are clickable.
 */
export interface SearchRow {
  kind: string;
  label: string;
  evidence: string;
  to: string | null;
}

/**
 * Counts arrive from a `LIMIT 1000` probe, so a capped count is a floor, not a total. Rendering
 * "1000" for a value that appears in two million rows would be a lie; "1000+" is the truth we
 * actually paid for.
 */
export function formatCount(count: number, capped: boolean): string {
  return capped ? `${count}+` : String(count);
}

const ROLE_LABELS: Record<string, string> = {
  feePayer: "fee payer",
  msgSender: "sender",
  callee: "callee",
  l2ToL1Sender: "L2→L1 sender",
  logEmitter: "log emitter",
  classLogEmitter: "class log emitter",
};

const LOG_SCOPE_LABELS: Record<string, string> = {
  private: "private",
  public: "public",
  contractClass: "contract class",
};

const L2_TO_L1_MATCH_LABELS: Record<string, string> = {
  content: "content",
  sender: "sender",
  messageHash: "message hash",
  recipient: "recipient",
};

/**
 * "in tx 0xabcd...1234" for a single hit, "in tx 0xabcd...1234 +6 more" for several.
 *
 * The named tx is the one the row navigates to, so the evidence and the destination agree: a bare
 * "in 7 txs" said nothing about where clicking would land. The "+N" carries the cap marker from
 * `formatCount` when the probe hit its ceiling — "+9+ more" is deliberate, since the row cannot
 * know how many were left behind and the results page is the honest answer.
 */
function txEvidence(matches: { txHash: string }[], capped = false): string {
  if (matches.length === 0) return "no transactions";
  const first = `in tx ${abbreviateHex(matches[0].txHash)}`;
  if (matches.length === 1) return first;
  return `${first} +${formatCount(matches.length - 1, capped)} more`;
}

function txTarget(network: string, matches: { txHash: string }[]): string | null {
  return matches.length > 0 ? `/${network}/tx/${matches[0].txHash}` : null;
}

function addressTarget(network: string, address: string): string {
  return `/${network}/murder-board?address=${encodeURIComponent(address)}`;
}

/**
 * Turn one server interpretation into a row. Kept pure and exported so the routing table — the
 * part that silently breaks when a page moves — is unit-testable without mounting the box.
 */
export function interpretationRow(
  interpretation: SearchInterpretation,
  network: string,
): SearchRow {
  switch (interpretation.kind) {
    case "tx":
      return {
        kind: "Transaction",
        label: abbreviateHex(interpretation.txHash),
        evidence:
          interpretation.blockNumber != null
            ? `${interpretation.status}, block ${interpretation.blockNumber}`
            : interpretation.status,
        to: `/${network}/tx/${interpretation.txHash}`,
      };

    case "block":
      return {
        kind: "Block",
        label: `Block ${interpretation.blockNumber}`,
        evidence:
          interpretation.matchedBy === "hash"
            ? `hash match, ${interpretation.numTxs} txs`
            : `${interpretation.numTxs} txs`,
        to: `/${network}/block/${interpretation.blockNumber}`,
      };

    case "address": {
      // "fee payer of 12 txs, callee in 3" — the unit is stated once and then carried, because
      // repeating "txs" for every role turns a five-role address into an unreadable row.
      const roles = interpretation.roles
        .map((r, i) => {
          const count = formatCount(r.txCount, r.capped);
          const name = ROLE_LABELS[r.role] ?? r.role;
          return i === 0 ? `${name} of ${count} txs` : `${name} in ${count}`;
        })
        .join(", ");
      return {
        kind: "Address",
        label: interpretation.label ?? abbreviateHex(interpretation.address),
        evidence: roles === "" ? "seen on this network" : roles,
        to: addressTarget(network, interpretation.address),
      };
    }

    case "calldataValue":
      return {
        kind: "Calldata value",
        label: abbreviateHex(interpretation.value),
        evidence: `appears in calldata of ${formatCount(interpretation.txCount, interpretation.capped)} txs`,
        to: addressTarget(network, interpretation.value),
      };

    case "nullifier":
      return {
        kind: "Nullifier",
        label: abbreviateHex(interpretation.value),
        evidence: txEvidence(interpretation.matches),
        to: txTarget(network, interpretation.matches),
      };

    case "noteHash":
      return {
        kind: "Note hash",
        label: abbreviateHex(interpretation.value),
        evidence: txEvidence(interpretation.matches),
        to: txTarget(network, interpretation.matches),
      };

    case "logField":
      return {
        kind: "Log field",
        label: `${LOG_SCOPE_LABELS[interpretation.scope] ?? interpretation.scope} log`,
        evidence: txEvidence(interpretation.matches, interpretation.capped),
        to: txTarget(network, interpretation.matches),
      };

    case "l2ToL1Message":
      return {
        kind: "L2→L1 message",
        label: `${L2_TO_L1_MATCH_LABELS[interpretation.matchedBy] ?? interpretation.matchedBy} match`,
        evidence: txEvidence(interpretation.matches, interpretation.capped),
        to: txTarget(network, interpretation.matches),
      };

    case "publicDataWrite":
      return {
        kind: "Public data write",
        label: interpretation.matchedBy === "leafSlot" ? "leaf slot match" : "value match",
        evidence: txEvidence(interpretation.matches, interpretation.capped),
        to: txTarget(network, interpretation.matches),
      };

    case "contractClass":
      return {
        kind: "Contract class",
        label: abbreviateHex(interpretation.contractClassId),
        evidence: txEvidence(interpretation.matches),
        to: txTarget(network, interpretation.matches),
      };

    case "blockProducer": {
      const role = interpretation.role === "coinbase" ? "coinbase" : "fee recipient";
      const count = formatCount(interpretation.blockCount, interpretation.capped);
      return {
        kind: "Block producer",
        label: abbreviateHex(interpretation.address),
        evidence:
          interpretation.latestBlockNumber != null
            ? `${role} of ${count} blocks, latest ${interpretation.latestBlockNumber}`
            : `${role} of ${count} blocks`,
        // The latest block it produced is the only concrete page to land on; without one there
        // is nothing to show, so the row stays informational.
        to:
          interpretation.latestBlockNumber != null
            ? `/${network}/block/${interpretation.latestBlockNumber}`
            : null,
      };
    }

    case "validator": {
      const registered = interpretation.isValidator ? "registered validator" : "not registered";
      const count = formatCount(interpretation.proposedSlotCount, interpretation.capped);
      return {
        kind: "Validator",
        label: abbreviateHex(interpretation.address),
        evidence:
          interpretation.latestProposedSlot != null
            ? `${registered}, proposed ${count} slots, latest ${interpretation.latestProposedSlot}`
            : registered,
        to: `/${network}/health`,
      };
    }

    case "slot":
      return {
        kind: "Slot",
        label: `Slot ${interpretation.slotNumber}`,
        // Health is an epoch-shaped page, so a slot lands on the epoch that contains it.
        evidence: `epoch ${interpretation.epoch}`,
        to: `/${network}/health?epoch=${interpretation.epoch}`,
      };

    case "epoch":
      return {
        kind: "Epoch",
        label: `Epoch ${interpretation.epoch}`,
        evidence: interpretation.isCurrent ? "current" : "",
        to: `/${network}/health?epoch=${interpretation.epoch}`,
      };

    case "checkpoint":
      return {
        kind: "Checkpoint",
        label: `Checkpoint ${interpretation.checkpointNumber}`,
        evidence:
          interpretation.blockCount != null ? `${interpretation.blockCount} blocks` : "",
        // No checkpoint detail page yet; the block list is the closest thing to its contents.
        to: `/${network}/blocks`,
      };

    case "label":
      return {
        kind: "Label",
        label: interpretation.label,
        evidence:
          interpretation.contractType != null
            ? `${interpretation.contractType} ${abbreviateHex(interpretation.address)}`
            : abbreviateHex(interpretation.address),
        to: addressTarget(network, interpretation.address),
      };

    case "functionSelector":
      return {
        kind: "Function selector",
        label: interpretation.selector,
        evidence: `called in ${formatCount(interpretation.txCount, interpretation.capped)} txs`,
        to:
          interpretation.latestTxHash != null
            ? `/${network}/tx/${interpretation.latestTxHash}`
            : null,
      };

    case "rollupContract":
      return {
        kind: "Rollup contract",
        label: abbreviateHex(interpretation.address),
        evidence: "rollup contract for this network",
        to: null,
      };
  }
}

export function searchRows(
  interpretations: SearchInterpretation[],
  network: string,
): SearchRow[] {
  return interpretations.map((i) => interpretationRow(i, network));
}

/**
 * The escape hatch at the bottom of the list. Every row above it jumps to one match — the first
 * one the probe returned — which is the right default and the wrong answer when the value shows up
 * in forty transactions. This row hands the same query to the results page, which lists them all.
 */
export function seeAllRow(network: string, query: string): SearchRow {
  return {
    kind: "See all",
    label: "Full results",
    evidence: "every match on one page",
    to: `/${network}/search?q=${encodeURIComponent(query)}`,
  };
}

const Wrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 420px;

  ${media.md} {
    max-width: none;
  }
`;

const Field = styled.input`
  width: 100%;
  background: ${theme.colors.bg};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  color: ${theme.colors.text};
  padding: 6px 10px;
  font-size: ${theme.fontSize.sm};
  font-family: inherit;

  &::placeholder {
    color: ${theme.colors.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Results = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  margin: 0;
  padding: ${theme.spacing.xs};
  list-style: none;
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  max-height: 60vh;
  overflow-y: auto;
`;

const Row = styled.li<{ active: boolean; disabled: boolean }>`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.sm};
  padding: 6px 8px;
  border-radius: ${theme.radius.sm};
  font-size: ${theme.fontSize.sm};
  cursor: ${(p) => (p.disabled ? "default" : "pointer")};
  color: ${(p) => (p.disabled ? theme.colors.textMuted : theme.colors.text)};
  opacity: ${(p) => (p.disabled ? 0.6 : 1)};
  background: ${(p) => (p.active && !p.disabled ? theme.colors.bgHover : "transparent")};
`;

/** The last row is a different kind of thing — a way out of the list, not an item in it. */
const SeeAll = styled(Row)`
  margin-top: ${theme.spacing.xs};
  border-top: 1px solid ${theme.colors.border};
  border-radius: 0 0 ${theme.radius.sm} ${theme.radius.sm};
  padding-top: 8px;
`;

/** The kind badge. Fixed width so the labels beside it line up down the list. */
const Badge = styled.span`
  flex-shrink: 0;
  min-width: 116px;
  white-space: nowrap;
  font-size: ${theme.fontSize.xs};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.colors.primary};
`;

const Label = styled.span`
  font-family: "SF Mono", "Fira Code", monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Evidence = styled.span`
  margin-left: auto;
  padding-left: ${theme.spacing.sm};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  white-space: nowrap;
`;

const Empty = styled.li`
  padding: 6px 8px;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  line-height: 1.6;
`;

const LoadingRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: 6px 8px;
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const SmallSpinner = styled(Spinner)`
  width: 12px;
  height: 12px;
  border-width: 2px;
`;

/** Full-screen on mobile: a 200px input inside a 375px header is unusable. */
const MobileSheet = styled.div`
  position: fixed;
  inset: 0;
  z-index: 300;
  background: ${theme.colors.bg};
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: ${theme.spacing.xs};
`;

const SheetResults = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  overflow-y: auto;
`;

export function Search({ variant = "desktop" }: { variant?: "desktop" | "mobile" }) {
  const navigate = useNavigate();
  const { selectedNetwork } = useNetworkStore();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const trimmed = query.trim();

  // Typing is worth one request, not one per keystroke — the probes fan out over a dozen tables.
  // Clearing the box skips the wait: there is nothing to ask for, and stale rows lingering under
  // an empty input read as a bug.
  useEffect(() => {
    if (trimmed === "") {
      setDebounced("");
      return;
    }
    const timer = setTimeout(() => setDebounced(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);

  const { data, isFetching, isError } = useSearch(selectedNetwork, debounced, trimmed !== "");

  // The see-all row rides along with the interpretations rather than being rendered beside them,
  // so arrow keys reach it and Enter follows it like any other row. It is appended only when there
  // is something to see all of, which makes "the last row is the see-all row" an invariant the
  // renderer below relies on.
  const rows = useMemo(() => {
    if (!data || data.interpretations.length === 0) return [];
    return [
      ...searchRows(data.interpretations, selectedNetwork),
      seeAllRow(selectedNetwork, data.query),
    ];
  }, [data, selectedNetwork]);

  // Anything typed since the last response leaves the rows describing an older query, so they are
  // held back rather than shown against the wrong input.
  const stale = debounced !== trimmed || (isFetching && !data);
  const visibleRows = stale ? [] : rows;

  // "/" focuses the box, the way every code-search UI does. Skipped while the user is already
  // typing somewhere, so it never eats a slash meant for a form.
  useEffect(() => {
    if (variant !== "desktop") return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (e.key === "/" && !typing) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [variant]);

  // Click-away, so the dropdown doesn't linger over the page.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const go = (row: SearchRow) => {
    if (!row.to) return;
    navigate(row.to);
    setQuery("");
    setOpen(false);
    setSheetOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const usable = visibleRows.filter((r) => r.to);
    if (e.key === "Escape") {
      setOpen(false);
      setSheetOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, visibleRows.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const picked = visibleRows[active];
      if (picked?.to) go(picked);
      else if (usable.length > 0) go(usable[0]);
    }
  };

  const body = (
    <>
      {visibleRows.map((row, i) => {
        // A non-empty list always ends in the see-all row (see the memo above).
        const RowElement = i === visibleRows.length - 1 ? SeeAll : Row;
        return (
          <RowElement
            key={`${row.kind}-${i}`}
            active={i === active}
            disabled={!row.to}
            onMouseEnter={() => setActive(i)}
            onClick={() => go(row)}
          >
            <Badge>{row.kind}</Badge>
            <Label>{row.label}</Label>
            {row.evidence !== "" && <Evidence>{row.evidence}</Evidence>}
          </RowElement>
        );
      })}
      {stale && (
        <LoadingRow>
          <SmallSpinner />
          Searching…
        </LoadingRow>
      )}
      {/* A failed request is not the same as a query nobody matched; saying "no matches" for a
          500 would send people off looking for a hash they typed correctly. */}
      {!stale && isError && <Empty>Search is unavailable right now.</Empty>}
      {!stale && !isError && visibleRows.length === 0 && <Empty>No matches.</Empty>}
    </>
  );

  // Nothing typed means nothing to offer: an empty panel hanging under the box is just noise.
  const showResults = trimmed !== "";

  if (variant === "mobile") {
    return (
      <>
        <CloseButton onClick={() => setSheetOpen(true)} aria-label="Search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </CloseButton>
        {sheetOpen && (
          <MobileSheet>
            <SheetHeader>
              <Field
                ref={inputRef}
                autoFocus
                value={query}
                placeholder="Hash, address, block, slot, epoch…"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
              />
              <CloseButton onClick={() => setSheetOpen(false)} aria-label="Close search">
                ✕
              </CloseButton>
            </SheetHeader>
            {showResults && <SheetResults>{body}</SheetResults>}
          </MobileSheet>
        )}
      </>
    );
  }

  return (
    <Wrapper ref={wrapperRef}>
      <Field
        ref={inputRef}
        value={query}
        placeholder="Search  /  hash, address, block, slot, epoch"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && showResults && <Results>{body}</Results>}
    </Wrapper>
  );
}
